import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { itApi } from './apiClient';
import { Alert, ItCtx, useIt } from './common';
import { AddAssetsDialog, AddEventDialog, AddLocationDialog, AddPersonDialog, AddReceiptDialog, AssetDialog, CheckInDialog, CheckoutDialog, DeleteDialog, EditDialog, EventDialog, ExportDialog, LinkReceiptDialog, MoveDialog, ReceiptDialog, RemoveLocationDialog, RemovePersonDialog, StatusDialog } from './dialogs';
import { AssetsTab, EventsTab, LoansTab, LocationsTab, PeopleTab, ReceiptsTab, ScanTab } from './tabs';
import { TABS, TAB_IDS, parseRoute, recordPath, tabPath } from './routes';

const remembered = () => { try { const t = localStorage.getItem('it-assets-tab'); return TAB_IDS.includes(t) ? t : 'assets'; } catch { return 'assets'; } };

// An event opened by its address: the list row carries the names the dialog
// shows, so load the list and pick the one.
// The page header and tab strip stay at the top while the list scrolls, and each tab's toolbar
// (or card header) and table header stack under them. Sticky offsets need the heights above, which
// change with the window width and the tab, so they are measured and written as CSS variables.
export function usePinnedHeights() {
  const [el, setEl] = useState(null);           // a callback ref: the section mounts after the meta loads
  useEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const top = el.querySelector('.uh-ia-top');
      const bar = el.querySelector('.uh-card > .uh-ia-tbar, .uh-card > .uh-card-h');
      el.style.setProperty('--uh-ia-top', `${top ? top.offsetHeight : 0}px`);
      el.style.setProperty('--uh-ia-tbar', `${bar ? bar.offsetHeight : 0}px`);
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const mo = new MutationObserver(measure);
    mo.observe(el, { childList: true, subtree: true });
    measure();
    return () => { ro.disconnect(); mo.disconnect(); };
  }, [el]);
  return setEl;
}

function EventByUrl({ id, onClose }) {
  const { notify } = useIt();
  const [event, setEvent] = useState(null);
  useEffect(() => {
    let live = true;
    itApi.events().then((r) => {
      if (!live) return;
      const e = (r.events || []).find((x) => x.id === id);
      if (e) setEvent(e); else { notify('That event is not on the list any more'); onClose(); }
    }, () => { if (live) onClose(); });
    return () => { live = false; };
  }, [id, onClose, notify]);
  return event ? <EventDialog event={event} onClose={onClose} /> : null;
}

export default function ItAssets() {
  // The view lives in the address: /it-assets/<tab>[/<id>]. Bare /it-assets (or an
  // unknown tab) becomes the last-used tab so the address bar always holds a real link.
  const { tab: tabParam, id: idParam } = useParams();
  const navigate = useNavigate();
  const here = useLocation();
  const { tab, record } = useMemo(() => parseRoute(tabParam, idParam), [tabParam, idParam]);   // stable per address, so callbacks built on it are too
  useEffect(() => { if (!tab) navigate(tabPath(remembered()), { replace: true }); }, [tab, navigate]);
  useEffect(() => { if (tab) { try { localStorage.setItem('it-assets-tab', tab); } catch { /* the tab just is not remembered */ } } }, [tab]);
  // Where closing a record dialog returns to: the list it was opened from, else its tab.
  const back = record ? ((here.state && here.state.back) || tabPath(tab)) : here.pathname + here.search;

  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [scope, setScope] = useState(null);      // counts for what the Assets tab is showing: { counts, filtered }
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);
  const [version, setVersion] = useState(0);
  const [toast, setToast] = useState('');

  const reloadMeta = useCallback(() => itApi.meta().then((m) => { setMeta(m); return m; }), []);
  useEffect(() => { reloadMeta().catch((e) => setError(e.message)); }, [reloadMeta]);
  useEffect(() => { itApi.stats().then(setStats, () => {}); }, [version]);
  useEffect(() => { if (!toast) return undefined; const t = setTimeout(() => setToast(''), 2600); return () => clearTimeout(t); }, [toast]);

  const refresh = useCallback(() => { setVersion((v) => v + 1); reloadMeta().catch(() => {}); }, [reloadMeta]);
  const closeDialog = useCallback(() => { setDialog(null); if (record) navigate(back); }, [record, back, navigate]);
  const openDialog = useCallback((d) => {
    if (d.type === 'refresh') { refresh(); return; }
    // The list's filters ride along on the record address, so the list underneath keeps them.
    if (d.type === 'asset' || d.type === 'receipt' || d.type === 'event') { setDialog(null); navigate(recordPath(d) + here.search, { state: { back } }); return; }
    setDialog(d);
  }, [refresh, navigate, back, here.search]);
  const ctx = useMemo(() => meta && ({
    meta, canManage: meta.can_manage, isAdmin: meta.is_admin, openDialog,
    closeDialog, refresh, reloadMeta, notify: setToast, version, setScope,
  }), [meta, version, refresh, reloadMeta, openDialog, closeDialog]);

  const pick = (t) => navigate(tabPath(t));
  const pageRef = usePinnedHeights();
  if (error) return <section className="uh-page"><Alert>{error}</Alert></section>;
  if (!ctx) return <section className="uh-page"><div className="uh-empty">Loading…</div></section>;

  if (!tab) return null;                       // redirecting to the remembered tab
  const d = dialog || record;                  // an action dialog sits over a linked record; closing returns to the list
  return (
    <ItCtx.Provider value={ctx}>
      <section className="uh-page uh-ia-page" ref={pageRef}>
        <div className="uh-ia-top">
        <div className="uh-ph">
          <div>
            <h1 className="uh-h1">IT assets</h1>
            <div className="uh-sub">Every device and accessory, who or where it is, and what is free to deploy or lend.</div>
          </div>
          {stats && (
            <div className="uh-ia-stats" title={tab === 'assets' && scope && scope.filtered ? 'Counts for the current search and filters' : undefined}>
              {(() => { const s = tab === 'assets' && scope ? scope.counts : stats; return [[s.total, scope && tab === 'assets' && scope.filtered ? 'Matching' : 'Assets'], [s.stock, 'In stock'], [s.deployed, 'Deployed'], [s.loan, 'On loan'], [s.repair, 'In repair']]; })().map(([v, k]) => (
                <div className="stat" key={k}><span className="v uh-num">{v}</span><span className="k">{k}</span></div>
              ))}
            </div>
          )}
        </div>
        <div className="uh-ia-tabs" role="tablist">
          {TABS.map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={tab === id} className="uh-ia-tab" onClick={() => pick(id)}>{label}</button>)}
        </div>
        </div>
        {tab === 'assets' && <AssetsTab mode="assets" />}
        {tab === 'available' && <AssetsTab mode="available" />}
        {tab === 'loans' && <LoansTab />}
        {tab === 'events' && <EventsTab />}
        {tab === 'receipts' && <ReceiptsTab />}
        {tab === 'locations' && <LocationsTab />}
        {tab === 'people' && <PeopleTab />}
        {tab === 'scan' && <ScanTab />}

        {d && d.type === 'asset' && <AssetDialog key={`a${d.id}`} id={d.id} onClose={closeDialog} />}
        {d && d.type === 'checkout' && <CheckoutDialog assets={d.assets} kind={d.kind} onClose={closeDialog} />}
        {d && d.type === 'move' && <MoveDialog assets={d.assets} onClose={closeDialog} />}
        {d && d.type === 'checkin' && <CheckInDialog assets={d.assets} onClose={closeDialog} />}
        {d && d.type === 'link' && <LinkReceiptDialog assets={d.assets} onClose={closeDialog} />}
        {d && d.type === 'addAssets' && <AddAssetsDialog receipt={d.receipt} onClose={closeDialog} />}
        {d && d.type === 'delete' && <DeleteDialog assets={d.assets} onClose={closeDialog} />}
        {d && d.type === 'status' && <StatusDialog asset={d.asset} onClose={closeDialog} />}
        {d && d.type === 'edit' && <EditDialog asset={d.asset} onClose={closeDialog} />}
        {d && d.type === 'receipt' && <ReceiptDialog key={`r${d.id}`} id={d.id} onClose={closeDialog} />}
        {d && d.type === 'addReceipt' && <AddReceiptDialog onClose={closeDialog} />}
        {d && d.type === 'addPerson' && <AddPersonDialog onClose={closeDialog} />}
        {d && d.type === 'removePerson' && <RemovePersonDialog person={d.person} onClose={closeDialog} />}
        {d && d.type === 'addLocation' && <AddLocationDialog onClose={closeDialog} />}
        {d && d.type === 'removeLocation' && <RemoveLocationDialog location={d.location} onClose={closeDialog} />}
        {d && d.type === 'addEvent' && <AddEventDialog onClose={closeDialog} />}
        {d && d.type === 'event' && (d.event ? <EventDialog event={d.event} onClose={closeDialog} /> : <EventByUrl key={`e${d.id}`} id={d.id} onClose={closeDialog} />)}
        {d && d.type === 'export' && <ExportDialog from={d.from} to={d.to} card={d.card} onClose={closeDialog} />}
        {toast && <div className="uh-ia-toast" role="status">{toast}</div>}
      </section>
    </ItCtx.Provider>
  );
}
